import { useCallback, useEffect, useState, type ReactNode } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ShareIcon from "@mui/icons-material/Share";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useSnackbar } from "@/components/Snackbar";
import { createShareLink, refreshShareSnapshot } from "@/apis/bacchus/segment";
import {
  isShareLinkActive,
  type CreateShareLinkBody,
  type ShareLink,
} from "@/apis/bacchus/shareTypes";

const COPIED_FEEDBACK_MS = 1_500;
const DEFAULT_EXPIRY_DAYS = 30;
const MAX_EXPIRY_DAYS = 365;
const MS_PER_DAY = 86_400_000;

/** An active link keeps its remaining lifetime as the starting point, so
 * updating it doesn't silently shorten a long-lived one. */
function initialExpiryDays(link: ShareLink | null): number {
  if (!link || !isShareLinkActive(link)) return DEFAULT_EXPIRY_DAYS;
  const remaining = Math.ceil(
    (new Date(link.expires_at).getTime() - Date.now()) / MS_PER_DAY
  );
  return Math.min(Math.max(remaining, 1), MAX_EXPIRY_DAYS);
}

const CAN_DO_ITEMS = [
  "Switch between the scoring models on this segment",
  "Select tiers",
  "Narrow the geography within this segment",
];

const FIXED_ITEMS = ["Segment filters", "Spend categories and their weights"];

interface IconListProps {
  items: string[];
  icon: ReactNode;
}

/** Dense info list shared by the "what they can do" / "what stays fixed" sections. */
function IconList({ items, icon }: IconListProps) {
  return (
    <List dense disablePadding>
      {items.map((item) => (
        <ListItem key={item} disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}>{icon}</ListItemIcon>
          <ListItemText primary={item} />
        </ListItem>
      ))}
    </List>
  );
}

interface ShareLinkDialogProps {
  open: boolean;
  segmentId?: string;
  /** Frozen at click time from the tab's current spend-category/tier/
   * corporate-parent selection — see MarketSizingContentHandle.getShareConfig. */
  config?: CreateShareLinkBody["config"];
  /** The segment's current link, if any — updated in place instead of a new one. */
  existingLink: ShareLink | null;
  onLinkChange: (link: ShareLink) => void;
  onClose: () => void;
}

export function ShareLinkDialog({
  open,
  segmentId,
  config,
  existingLink,
  onLinkChange,
  onClose,
}: ShareLinkDialogProps) {
  const { showError } = useSnackbar();
  const [isCreating, setIsCreating] = useState(false);
  // Set once the user creates/updates during this open; until then the
  // dialog shows `existingLink`.
  const [savedLink, setSavedLink] = useState<ShareLink | null>(null);
  const [copied, setCopied] = useState(false);
  // Kept as text so the field can be cleared while typing.
  const [expiryDays, setExpiryDays] = useState(String(DEFAULT_EXPIRY_DAYS));
  const days = Number(expiryDays);
  const expiryValid =
    /^\d+$/.test(expiryDays) && days >= 1 && days <= MAX_EXPIRY_DAYS;
  const shareLink = savedLink ?? existingLink;
  const isUpdate = existingLink !== null && savedLink === null;

  useEffect(() => {
    if (open) {
      setSavedLink(null);
      setCopied(false);
      setExpiryDays(String(initialExpiryDays(existingLink)));
    }
    // Only on open: re-seeding when the link changes after a save would wipe
    // the saved state the dialog is showing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleCreate = useCallback(async () => {
    if (!segmentId || !config || !expiryValid) return;
    setIsCreating(true);
    try {
      const link = await createShareLink(segmentId, {
        config,
        expires_in_days: days,
      });
      setSavedLink(link);
      onLinkChange(link);
      // Saving is how the owner pulls fresh data into the share page. The
      // snapshot precomputes the panels; a /warm here only ran them live again.
      refreshShareSnapshot(segmentId, { force: true }).catch(() => {});
    } catch (err) {
      console.error("Failed to save share link:", err);
      showError(
        `Couldn't ${isUpdate ? "update" : "create"} the share link. Please try again.`
      );
    } finally {
      setIsCreating(false);
    }
  }, [segmentId, config, expiryValid, days, isUpdate, onLinkChange, showError]);

  const shareUrl = shareLink?.token
    ? `${window.location.origin}/share/${shareLink.token}`
    : "";

  const handleCopy = useCallback(() => {
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
      })
      .catch(() => {
        showError("Couldn't copy the link — copy it manually instead.");
      });
  }, [shareUrl, showError]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      id="market-sizing-share-dialog"
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ShareIcon fontSize="small" />
          Share Market Sizing
        </Box>
        <IconButton
          aria-label="close"
          data-testid="market-sizing-share-dialog-close-btn"
          onClick={onClose}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {isUpdate
            ? "This segment already has a link. Updating applies the current Market Sizing settings to it — the URL stays the same and the expiry restarts from today."
            : "Create a view-only public link. No sign-in needed."}
        </Typography>

        {!savedLink && (
          <>
            <Typography variant="subtitle2" color="text.secondary">
              What they can do
            </Typography>
            <IconList
              items={CAN_DO_ITEMS}
              icon={<CheckCircleOutlineIcon color="success" fontSize="small" />}
            />

            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mt: 2 }}
            >
              What stays fixed
            </Typography>
            <IconList
              items={FIXED_ITEMS}
              icon={<LockOutlinedIcon color="action" fontSize="small" />}
            />

            <TextField
              label="Link expires after (days)"
              type="number"
              value={expiryDays}
              onChange={(e) => setExpiryDays(e.target.value)}
              error={!expiryValid}
              helperText={
                <span data-testid="market-sizing-share-expiry-days-helper">
                  {expiryValid
                    ? `${isUpdate ? "New expiry" : "Expires on"}: ${new Date(Date.now() + days * MS_PER_DAY).toLocaleDateString()}`
                    : `Enter a whole number of days from 1 to ${MAX_EXPIRY_DAYS}`}
                </span>
              }
              size="small"
              sx={{ mt: 2, width: 220 }}
              inputProps={{
                min: 1,
                max: MAX_EXPIRY_DAYS,
                step: 1,
                "data-testid": "market-sizing-share-expiry-days-input",
              }}
            />
          </>
        )}

        <Alert
          severity="info"
          data-testid="market-sizing-share-info-alert"
          sx={{ my: 2 }}
        >
          Anyone with the link can view it until it expires, and later edits to
          this segment also appear in the shared view.
        </Alert>

        {shareLink && !shareUrl && (
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="market-sizing-share-url-unavailable"
          >
            This link's URL can no longer be displayed. Updating it generates a
            new URL.
          </Typography>
        )}

        {shareUrl && shareLink && (
          <>
            <TextField
              id="market-sizing-share-url-input"
              inputProps={{ "data-testid": "market-sizing-share-url-input" }}
              value={shareUrl}
              fullWidth
              size="small"
              slotProps={{
                input: {
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleCopy}
                        disabled={!isShareLinkActive(shareLink)}
                        aria-label={copied ? "Copied" : "Copy share link"}
                        data-testid="market-sizing-share-copy-btn"
                      >
                        {copied ? (
                          <CheckIcon fontSize="small" color="success" />
                        ) : (
                          <ContentCopyIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1 }}
              data-testid="market-sizing-share-expires-label"
            >
              {shareLink.revoked_at
                ? "Revoked — update the link to reactivate it."
                : isShareLinkActive(shareLink)
                  ? `Expires on ${new Date(shareLink.expires_at).toLocaleDateString()}`
                  : `Expired on ${new Date(shareLink.expires_at).toLocaleDateString()} — update the link to reactivate it.`}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ padding: 2 }}>
        {savedLink ? (
          <Button
            variant="contained"
            color="primary"
            onClick={onClose}
            data-testid="market-sizing-share-close-btn"
          >
            Done
          </Button>
        ) : (
          <>
            <Button
              variant="outlined"
              color="secondary"
              onClick={onClose}
              data-testid="market-sizing-share-close-btn"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreate}
              disabled={isCreating || !segmentId || !config || !expiryValid}
              data-testid="market-sizing-share-create-btn"
            >
              {isUpdate ? "Update Shareable Link" : "Create link"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
